import { constants } from '@eti/utils';
import groupArray from 'group-array';
import isPlainObj from 'is-plain-obj';
import type { TaxCategoryType } from '@eti/schema-types';
import { RebookEligibility } from '../pages/order-details/components/manage-trip/rebooking/v1/models';
import type { RebookEligibilityType } from '../pages/order-details/components/manage-trip/rebooking/v1/models';

type Money = {
  value: number;
};

type Traveler = {
  id?: string | null;
  ageType: string;
};

type TaxesAndFees = {
  category: TaxCategoryType;
  taxes: ({
    amount: Money;
    code: string;
    title: string;
  } | null)[];
};

type TravelerPrice = {
  travelerId?: string | null;
  price?: {
    markup?: Money | null;
    price: Money;
    vat?: Money | null;
  } | null;
  taxesAndFees?: (TaxesAndFees | null)[] | null;
};

type RebookingTravelerPrice = {
  travelerId?: string | null;
  rebookingPrice?: {
    airlineFees?: Money | null;
    airlinePenalty?: Money | null;
    fareDifference?: Money | null;
    serviceFee?: Money | null;
    taxDifference?: Money | null;
    totalDifference?: Money | null;
  } | null;
};

const getTravelers = (travelers: (Traveler | null)[], travelerPrices: (TravelerPrice | null)[]) =>
  travelers
    .filter((traveler): traveler is Traveler => traveler !== null)
    .map((traveler, index) => {
      const { price, taxesAndFees } =
        travelerPrices.find((travelerPrice) => travelerPrice?.travelerId === traveler.id) || {};

      if (!isPlainObj(price)) {
        return {
          ...traveler,
          id: traveler.id || String(index),
          markup: 0,
          price: 0,
          vat: 0,
          taxesAndFees: [],
        };
      }

      const nonNullPrice = price as NonNullable<typeof price>;

      return {
        ...traveler,
        id: traveler.id || String(index),
        markup: nonNullPrice.markup && nonNullPrice.markup.value,
        price: nonNullPrice.price.value,
        vat: nonNullPrice.vat && nonNullPrice.vat.value,
        taxesAndFees,
      };
    });

export const getTravelerPrices = (
  travelers: (Traveler | null)[] = [],
  travelerPrices: (TravelerPrice | null)[] = [],
) => {
  const travelerGroups = groupArray(getTravelers(travelers, travelerPrices), 'ageType') as Record<
    string,
    ReturnType<typeof getTravelers>
  >;

  return Object.entries(travelerGroups).map(([ageType, items]) => ({
    ageType,
    markup: items[0].markup,
    price: items[0].price - (items[0].vat ?? 0) - (items[0].markup ?? 0),
    quantity: items.length,
    taxesAndFees: (items[0].taxesAndFees || [])
      .filter((entry): entry is TaxesAndFees => entry !== null)
      .map((entry) => ({
        ...entry,
        taxes: entry.taxes.filter((tax): tax is NonNullable<typeof tax> => tax !== null),
      })),
    totalPrice: items[0].price,
    totalPriceSum: items.reduce((total, { price }) => total + price, 0),
    vat: items[0].vat,
  }));
};

export const getTravelerPricesTotal = (travelerPrices: { totalPriceSum: number }[] = []) =>
  travelerPrices.reduce((total, { totalPriceSum }) => total + totalPriceSum, 0);

const { ageTypes } = constants;

export const getAgeTypeLabelForQuantity = (ageType: string | undefined, quantity = 0) => {
  const ageTypeLabels: Record<string, string> = {
    [ageTypes.ADULT]:
      quantity > 1 ? 'Air.Search.NumberOfAdultsPlural' : 'Air.Search.NumberOfAdultsSingular',
    [ageTypes.CHILD]:
      quantity > 1 ? 'Air.Search.NumberOfChildrenPlural' : 'Air.Search.NumberOfChildrenSingular',
    [ageTypes.INFANT]:
      quantity > 1 ? 'Air.Search.NumberOfInfantsPlural' : 'Air.Search.NumberOfInfantsSingular',
  };

  return ageTypeLabels[ageType ?? ''] || ageTypeLabels[ageTypes.ADULT];
};

export const getAgeTypeLabel = (ageType?: string) => {
  const ageTypeLabels: Record<string, string> = {
    [ageTypes.ADULT]: 'Adult',
    [ageTypes.CHILD]: 'Child',
    [ageTypes.INFANT]: 'Infant',
  };

  return ageTypeLabels[ageType ?? ''] || ageTypeLabels[ageTypes.ADULT];
};

const getRebookingTravelers = (
  travelers: Traveler[],
  travelerPrices: RebookingTravelerPrice[],
  rebookEligibilityType?: RebookEligibilityType,
) =>
  travelers.map((traveler, index) => {
    const { rebookingPrice } =
      travelerPrices.find((travelerPrice) => travelerPrice.travelerId === traveler.id) || {};

    if (!isPlainObj(rebookingPrice)) {
      return {
        ...traveler,
        id: traveler.id || String(index),
        airlineFees: 0,
        airlinePenalty: 0,
        fareDifference: 0,
        serviceFee: 0,
        taxDifference: 0,
        totalDifference: 0,
      };
    }

    return {
      ...traveler,
      id: traveler.id || String(index),
      airlineFees: rebookingPrice?.airlineFees?.value,
      airlinePenalty:
        rebookEligibilityType === RebookEligibility.CHANGE_FLEXIBLE_TICKET_YOURSELF
          ? 0
          : rebookingPrice?.airlinePenalty?.value,
      fareDifference: rebookingPrice?.fareDifference?.value,
      serviceFee:
        rebookEligibilityType === RebookEligibility.CHANGE_FLEXIBLE_TICKET_YOURSELF
          ? 0
          : rebookingPrice?.serviceFee?.value,
      taxDifference: rebookingPrice?.taxDifference?.value,
      totalDifference:
        rebookEligibilityType === RebookEligibility.CHANGE_FLEXIBLE_TICKET_YOURSELF
          ? (rebookingPrice?.totalDifference?.value || 0) -
            (rebookingPrice?.serviceFee?.value || 0) -
            (rebookingPrice?.airlinePenalty?.value || 0)
          : rebookingPrice?.totalDifference?.value,
    };
  });

export const getRebookingTravelerPrices = (
  travelers: Traveler[] = [],
  travelerPrices: RebookingTravelerPrice[] = [],
  rebookEligibilityType?: RebookEligibilityType,
) => {
  const travelerGroups = groupArray(
    getRebookingTravelers(travelers, travelerPrices, rebookEligibilityType),
    'ageType',
  ) as Record<string, ReturnType<typeof getRebookingTravelers>>;

  return Object.entries(travelerGroups).map(([ageType, items]) => ({
    ageType,
    airlineFees: items[0].airlineFees,
    airlinePenalty: items[0].airlinePenalty,
    fareDifference: items[0].fareDifference,
    quantity: items.length,
    serviceFee: items[0].serviceFee,
    taxDifference: items[0].taxDifference,
    totalDifference: items[0].totalDifference,
  }));
};
